class ExtraServices extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hidden: 'hidden',
      active: 'active',
      open: 'open',
      serviceHeader: '.extra-service__header',
      serviceCheckboxes: '.extra-service__input',
      serviceContentContainer: '.extra-service__content',
      serviceDescription: '.extra-service__description',
      spinner: '.loading-overlay__spinner',
    };

    this.toggleButton = this.querySelector('.extra-services__button');
    this.descriptionToggleButtons = this.querySelectorAll('.extra-service__description-btn');
    this.contentContainer = this.querySelector('.extra-services__content');
    this.notificationContainer = document.querySelector('.extra-services__notification');
    this.serviceCheckboxes = Array.from(this.querySelectorAll(this.classes.serviceCheckboxes));
    this.handlingFeeVariant =
      this.querySelector(`#cart-drawer-extra-service-${window.extraServices.low_order_value_service_fee_product}`)
        ?.dataset?.variantId || null;
    this.handlingFeeMinimum = window.extraServices.low_order_value * 100 || 1500;
    this.extendedReturnsVariant =
      this.querySelector(`#cart-drawer-extra-service-${window.extraServices.extended_returns_product}`)?.dataset
        ?.variantId || null;
    this.isSyncInProgress = false;
    this.isUpdatingFromSelf = false;
    this.isReadOnlySyncInProgress = false;
    this.lastProcessedCartToken = null;
    this.pendingOperations = new Map();
    this.processingVariants = new Set();
    this.cachedSections = null;

    this.serviceCheckboxes.forEach(
      (checkbox) =>
        checkbox.dataset.serviceType !== 'mandatory' &&
        checkbox.addEventListener('change', this.onServiceCheckboxToggle.bind(this)),
    );

    this.init();
  }

  init() {
    if (this.toggleButton && this.contentContainer) {
      this.toggleButton.addEventListener('click', this.contentToggleEventHandler.bind(this));
    }

    if (this.descriptionToggleButtons?.length) {
      this.descriptionToggleButtons.forEach((button) =>
        button.addEventListener('click', this.descriptionToggleEventHandler.bind(this)),
      );
    }
  }

  contentToggleEventHandler() {
    if (!this.contentContainer || !this.toggleButton) return;

    this.contentContainer.classList.toggle(this.classes.active);
    this.toggleButton.classList.toggle(this.classes.open);
  }

  descriptionToggleEventHandler(event) {
    const clicked = event.target;
    const parent = clicked.closest(this.classes.serviceContentContainer);
    if (!parent) return;

    const text = parent.querySelector(this.classes.serviceDescription);
    if (!text) return;

    if (parent.classList.contains(this.classes.active)) {
      parent.classList.remove(this.classes.active);
      text.setAttribute('aria-expanded', 'false');
    } else {
      parent.classList.add(this.classes.active);
      text.setAttribute('aria-expanded', 'true');
    }
  }

  async fetchCart() {
    return fetch(`${window.Shopify.routes.root}cart.js`).then(async (response) => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    });
  }

  async updateCartItem(variantId, quantity) {
    if (this.isVariantReplacementInProgress) {
      return;
    }

    const section = this.getSectionsToRender().map((section) => section.section);
    const data = { sections: section };

    const isAdd = quantity > 0;
    const endpoint = isAdd ? 'cart/add.js' : 'cart/change.js';
    const payload = isAdd
      ? { id: variantId, quantity, sections: data.sections }
      : { id: variantId, quantity: 0, sections: data.sections };

    const checkbox = document.querySelector(`input[data-variant-id="${variantId}"]`);
    const spinner = checkbox?.closest(this.classes.serviceHeader)?.querySelector(this.classes.spinner);

    return fetch(`${window.Shopify.routes.root}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Cart API error (${response.status}):`, errorText);
          throw new Error(`Cart API failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const responseJson = JSON.parse(await response.text());
        if (responseJson?.token) {
          this.lastProcessedCartToken = responseJson.token;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: isAdd ? 'cart-items-add' : 'cart-items',
          cartData: responseJson,
        });
        return responseJson;
      })
      .then((parsedResponse) => {
        spinner?.classList.add(this.classes.hidden);
        this.renderContents(parsedResponse);
        // sync checkbox states from cart response instead of relying on DOM re-render
        if (parsedResponse.items) {
          this.syncServiceCheckboxes(parsedResponse.items);
          // update handling fee visibility based on new cart total
          const serviceIds = this.serviceCheckboxes.map((cb) => cb.dataset.variantId);
          const nonServiceItems = parsedResponse.items.filter(
            (item) => !serviceIds.includes(item.variant_id.toString()),
          );
          const cartTotal = nonServiceItems.reduce((sum, item) => sum + (item.final_line_price || item.line_price), 0);
          this.updateHandlingFeeVisibility(cartTotal);
        }
        return parsedResponse;
      });
  }

  async addItemToCart(variantId, isAutomaticUpdate = false) {
    try {
      const cart = await this.fetchCart();
      const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
      if (cartVariantIds.includes(variantId)) {
        return cart;
      }
    } catch (error) {
      const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
      if (cartItemsVariantIds.includes(variantId)) {
        console.error('Variant found in cart:', variantId);
        return;
      }
    }

    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (isAutomaticUpdate && !wasAlreadyUpdating) {
      this.isUpdatingFromSelf = true;
    }

    return this.updateCartItem(variantId, 1)
      .then((cartData) => {
        if (cartData?.token) {
          this.lastProcessedCartToken = cartData.token;
        }
        return cartData;
      })
      .finally(() => {
        if (isAutomaticUpdate && !wasAlreadyUpdating) {
          this.isUpdatingFromSelf = false;
        }
      });
  }

  async removeItemFromCart(variantId, isAutomaticUpdate = false) {
    try {
      const cart = await this.fetchCart();
      const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
      if (!cartVariantIds.includes(variantId)) {
        return cart;
      }
    } catch (error) {
      const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
      if (!cartItemsVariantIds.includes(variantId)) {
        console.error('Variant not found in cart:', variantId);
        return;
      }
    }

    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (isAutomaticUpdate && !wasAlreadyUpdating) {
      this.isUpdatingFromSelf = true;
    }

    return this.updateCartItem(variantId, 0)
      .then((cartData) => {
        if (cartData?.token) {
          this.lastProcessedCartToken = cartData.token;
        }
        return cartData;
      })
      .finally(() => {
        if (isAutomaticUpdate && !wasAlreadyUpdating) {
          this.isUpdatingFromSelf = false;
        }
      });
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const elementToReplace = sectionElement?.querySelector(section.selector) || sectionElement;
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });

    this.cachedSections = null;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    if (this.cachedSections) {
      return this.cachedSections;
    }

    const cartDrawerElement = document.getElementById('CartDrawer');
    const mainCartElement = document.getElementById('main-cart');

    const sections = [
      {
        id: 'CartDrawer',
        section: cartDrawerElement?.dataset.id,
        selector: '.drawer__contents.js-contents',
        element: cartDrawerElement,
      },
      {
        id: 'main-cart',
        section: mainCartElement?.dataset.id,
        selector: '.main-cart--left',
        element: mainCartElement,
      },
      {
        id: 'main-cart',
        section: mainCartElement?.dataset.id,
        selector: '.cart__blocks',
        element: mainCartElement,
      },
    ];

    this.cachedSections = sections.filter((s) => s.section != null);
    return this.cachedSections;
  }

  onServiceCheckboxToggle(event) {
    const spinner = event.target?.closest(this.classes.serviceHeader)?.querySelector(this.classes.spinner);
    spinner?.classList.remove(this.classes.hidden);
    const checkbox = event.target;
    const variantId = checkbox.dataset.variantId;

    const targetState = checkbox.checked;
    const originalState = !targetState;
    checkbox.disabled = true;

    this.isUpdatingFromSelf = true;

    const operation = targetState ? this.addItemToCart(variantId) : this.removeItemFromCart(variantId);

    const promise = operation
      .then(async (cartData) => {
        const hasFullCart = cartData?.token && Array.isArray(cartData?.items);
        if (hasFullCart) {
          this.lastProcessedCartToken = cartData.token;
          return this.processCartContents(cartData);
        }
        const cart = await this.fetchCart();
        this.lastProcessedCartToken = cart.token;

        const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
        const isInCart = cartVariantIds.includes(variantId);

        if (targetState && !isInCart) {
          console.error('Service add operation failed - item not in cart');
          checkbox.checked = originalState;
        } else if (!targetState && isInCart) {
          console.error('Service remove operation failed - item still in cart');
          checkbox.checked = originalState;
        }
        return this.processCartContents(cart);
      })
      .then(() => {
        this.isUpdatingFromSelf = false;
      })
      .catch((error) => {
        console.error('Service toggle failed:', error);
        checkbox.checked = originalState;
        this.isUpdatingFromSelf = false;
      })
      .finally(() => {
        checkbox.disabled = false;
        this.pendingOperations.delete(variantId);
        if (this.pendingOperations.size === 0) {
          this.isUpdatingFromSelf = false;
        }
      });

    this.pendingOperations.set(variantId, promise);
    return promise;
  }

  updateServices() {
    if (this.isSyncInProgress) return;

    if (this.isVariantReplacementInProgress) {
      clearVariantReplacementFlagAfterSkip();
      return;
    }

    if (this.pendingOperations.size > 0 && !this.isUpdatingFromSelf) {
      return Promise.all(this.pendingOperations.values()).then(() => {
        return this.fetchCart().then((cart) => {
          if (cart.token !== this.lastProcessedCartToken) {
            this.lastProcessedCartToken = cart.token;
            return this.processCartContents(cart);
          }
        });
      });
    }

    this.isSyncInProgress = true;
    return this.fetchCart()
      .then((cart) => {
        if (cart.token !== this.lastProcessedCartToken) {
          this.lastProcessedCartToken = cart.token;
          return this.processCartContents(cart);
        }
      })
      .catch((error) => {
        console.error('Cart sync error:', error);
      })
      .finally(() => (this.isSyncInProgress = false));
  }

  processCartContents(cart) {
    const serviceIds = this.serviceCheckboxes.map((cb) => cb.dataset.variantId);
    const nonServiceItems = (cart.items || []).filter((item) => !serviceIds.includes(item.variant_id.toString()));
    const cartTotal = nonServiceItems.reduce((sum, item) => sum + (item.final_line_price || item.line_price), 0);

    if (nonServiceItems.length === 0) {
      this.updateHandlingFeeVisibility(0);

      const removalPromises = this.serviceCheckboxes
        .filter((cb) => cb.checked)
        .map((cb) => {
          cb.checked = false;
          return this.removeItemFromCart(cb.dataset.variantId).then(() => {
            this.showNotification(window.cartActions?.service_removed_html, 'warning', cb.dataset.serviceTitle);
          });
        });
      return Promise.all(removalPromises);
    }

    this.syncServiceCheckboxes(cart.items);
    this.updateHandlingFee(cartTotal, cart.items);
    this.addExtendedReturnsIfNeeded(cart.items);
  }

  addExtendedReturnsIfNeeded(cartItems) {
    if (!this.extendedReturnsVariant) return;

    const variantStr = this.extendedReturnsVariant.toString();
    if (this.processingVariants.has(variantStr)) return;
    if (cartItems.some((i) => i.variant_id.toString() === variantStr)) return;

    const checkbox = this.querySelector(`input[data-variant-id="${variantStr}"]`);
    this.processingVariants.add(variantStr);
    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (!wasAlreadyUpdating) this.isUpdatingFromSelf = true;
    if (checkbox) checkbox.checked = true;

    this.addItemToCart(this.extendedReturnsVariant, true).finally(() => {
      this.processingVariants.delete(variantStr);
      if (!wasAlreadyUpdating) this.isUpdatingFromSelf = false;
    });
  }

  updateHandlingFee(cartTotal, cartItems) {
    const handlingFeeCheckbox = this.querySelector(`input[data-variant-id="${this.handlingFeeVariant}"]`);
    if (!handlingFeeCheckbox) return;

    const handlingFeeVariantStr = this.handlingFeeVariant.toString();
    this.updateHandlingFeeVisibility(cartTotal);

    if (this.processingVariants.has(handlingFeeVariantStr)) {
      return;
    }

    const handlingFeePresent = cartItems.some((i) => i.variant_id.toString() === handlingFeeVariantStr);
    const shouldAddHandlingFee = cartTotal < this.handlingFeeMinimum && !handlingFeePresent;
    const shouldRemoveHandlingFee = cartTotal >= this.handlingFeeMinimum && handlingFeePresent;

    if (shouldAddHandlingFee) {
      this.processingVariants.add(handlingFeeVariantStr);
      const wasAlreadyUpdating = this.isUpdatingFromSelf;
      if (!wasAlreadyUpdating) {
        this.isUpdatingFromSelf = true;
      }
      handlingFeeCheckbox.checked = true;
      this.addItemToCart(this.handlingFeeVariant, true)
        .then((cartData) => {
          if (cartData?.token) {
            this.lastProcessedCartToken = cartData.token;
          }
          this.showNotification(
            window.cartActions?.service_added_html,
            'warning',
            handlingFeeCheckbox.dataset.serviceTitle,
          );
        })
        .catch((error) => {
          console.error('Failed to add handling fee:', error);
        })
        .finally(() => {
          this.processingVariants.delete(handlingFeeVariantStr);
          if (!wasAlreadyUpdating) {
            this.isUpdatingFromSelf = false;
          }
        });
    } else if (shouldRemoveHandlingFee) {
      this.processingVariants.add(handlingFeeVariantStr);
      const wasAlreadyUpdating = this.isUpdatingFromSelf;
      if (!wasAlreadyUpdating) {
        this.isUpdatingFromSelf = true;
      }
      handlingFeeCheckbox.checked = false;
      this.removeItemFromCart(this.handlingFeeVariant, true)
        .then((cartData) => {
          if (cartData?.token) {
            this.lastProcessedCartToken = cartData.token;
          }
          this.showNotification(
            window.cartActions?.service_removed_html,
            'success',
            handlingFeeCheckbox.dataset.serviceTitle,
          );
        })
        .catch((error) => {
          console.error('Failed to remove handling fee:', error);
        })
        .finally(() => {
          this.processingVariants.delete(handlingFeeVariantStr);
          if (!wasAlreadyUpdating) {
            this.isUpdatingFromSelf = false;
          }
        });
    }
  }

  updateHandlingFeeVisibility(cartTotal) {
    const handlingFeeCheckbox = this.querySelector(`input[data-variant-id="${this.handlingFeeVariant}"]`);
    if (!handlingFeeCheckbox) return;

    const handlingFeeContainer =
      handlingFeeCheckbox.closest('.extra-service__item') ||
      handlingFeeCheckbox.closest('.extra-service') ||
      handlingFeeCheckbox.closest('[data-service-type]');

    if (!handlingFeeContainer) return;

    const shouldShow = cartTotal < this.handlingFeeMinimum;

    if (shouldShow) {
      handlingFeeContainer.classList.remove(this.classes.hidden);
    } else {
      handlingFeeContainer.classList.add(this.classes.hidden);
    }
  }

  syncServiceCheckboxes(cartItems) {
    const cartVariantIds = cartItems.map((i) => i.variant_id.toString());
    this.serviceCheckboxes.forEach((cb) => {
      const shouldBeChecked = cartVariantIds.includes(cb.dataset.variantId);
      if (cb.checked !== shouldBeChecked) {
        cb.checked = shouldBeChecked;
      }
    });
  }

  showNotification(message, type = 'success', serviceTitle) {
    if (!this.createNotification || !this.createNotificationString) return;
    if (!message || !serviceTitle) return;

    const html = this.createNotificationString(message, serviceTitle, 1);
    this.createNotification(type, html);
  }

  createNotificationString(template, title, quantity) {
    return template.replace('{{ title }}', title).replace('{{ value }}', quantity);
  }

  createNotification(type, messageHtml) {
    if (!this.notificationContainer) return;

    const notificationClass = this.notificationContainer.dataset.notificationClass;
    const notificationSuccessClass = this.notificationContainer.dataset.notificationSuccessClass;
    const notificationWarningClass = this.notificationContainer.dataset.notificationWarningClass;
    const notificationAlertClass = this.notificationContainer.dataset.notificationAlertClass;

    const notification = document.createElement('div');
    notification.classList.add('text-x-small');

    if (type === 'success' && notificationClass && notificationSuccessClass) {
      notification.classList.add(notificationClass, notificationSuccessClass);
    } else if (type === 'warning' && notificationClass && notificationWarningClass) {
      notification.classList.add(notificationClass, notificationWarningClass);
    } else if (type === 'alert' && notificationClass && notificationAlertClass) {
      notification.classList.add(notificationClass, notificationAlertClass);
    }

    notification.innerHTML = messageHtml;
    this.notificationContainer.prepend(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}

customElements.define('extra-services', ExtraServices);

function clearVariantReplacementFlagAfterSkip() {
  queueMicrotask(() => {
    document.querySelectorAll('extra-services').forEach((el) => {
      el.isVariantReplacementInProgress = false;
    });
  });
}

function debounceExtraServices(fn, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const debouncedCartUpdate = debounceExtraServices((event) => {
  const extraServicesElements = document.querySelectorAll('extra-services');
  if (extraServicesElements.length === 0) return;

  const isAnyUpdating = Array.from(extraServicesElements).some(
    (el) => el.isUpdatingFromSelf || el.pendingOperations.size > 0 || el.isSyncInProgress,
  );

  if (isAnyUpdating) {
    return;
  }

  const handlingFeeVariant = extraServicesElements[0]?.handlingFeeVariant?.toString();
  if (handlingFeeVariant) {
    const isProcessingFee = Array.from(extraServicesElements).some((el) =>
      el.processingVariants?.has(handlingFeeVariant),
    );
    if (isProcessingFee) {
      return;
    }
  }

  if (event?.source === 'cart-items-add' || event?.source === 'cart-items') {
    if (event?.cartData?.token) {
      const hasProcessedToken = Array.from(extraServicesElements).some(
        (el) => el.lastProcessedCartToken === event.cartData.token,
      );
      if (hasProcessedToken) {
        return;
      }
    }
  }

  if (extraServicesElements.length > 0) {
    extraServicesElements[0].updateServices();
  }
}, 50);

subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  const extraServicesElements = document.querySelectorAll('extra-services');
  if (extraServicesElements.length > 0) {
    const isAnyUpdating = Array.from(extraServicesElements).some(
      (el) => el.isUpdatingFromSelf || el.pendingOperations.size > 0 || el.isSyncInProgress,
    );

    if (isAnyUpdating) {
      return;
    }

    if (event?.source === 'cart-items-add' || event?.source === 'cart-items') {
      if (event?.cartData?.token) {
        const hasProcessedToken = Array.from(extraServicesElements).some(
          (el) => el.lastProcessedCartToken === event.cartData.token,
        );
        if (hasProcessedToken) {
          return;
        }
      }
    }
  }

  debouncedCartUpdate(event);
});
